import axios from 'axios';

interface KYCApplicantData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  address: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

interface KYCResponse {
  success: boolean;
  data?: {
    applicantId: string;
    accessToken: string;
    status?: string;
    message?: string;
  };
  error?: string;
}

class KYCService {
  private baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  /**
   * Create a new KYC applicant and get access token
   */
  async createApplicant(userData: KYCApplicantData, username: string): Promise<KYCResponse> {
    try {
      const response = await axios.post(`${this.baseUrl}/kyc/applicant?username=${encodeURIComponent(username)}`, {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        phoneNumber: userData.phoneNumber,
        dateOfBirth: userData.dateOfBirth,
        address: userData.address
      });

      console.log('KYC Service - Raw response:', response.data);

      if (response.data.status === 'success' && response.data.data?.success) {
        return {
          success: true,
          data: {
            applicantId: response.data.data.applicantId,
            accessToken: response.data.data.accessToken
          }
        };
      } else {
        return {
          success: false,
          error: response.data.message || 'Failed to create KYC applicant'
        };
      }
    } catch (error: any) {
      console.error('KYC service error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create KYC applicant'
      };
    }
  }

  /**
   * Get KYC status for an applicant
   */
  async getKYCStatus(applicantId: string, username: string): Promise<KYCResponse> {
    try {
      const response = await axios.get(`${this.baseUrl}/kyc/status/${applicantId}?username=${encodeURIComponent(username)}`);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          error: response.data.message || 'Failed to get KYC status'
        };
      }
    } catch (error: any) {
      console.error('KYC status error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get KYC status'
      };
    }
  }

  /**
   * Refresh access token for an existing applicant
   */
  async refreshAccessToken(applicantId: string): Promise<KYCResponse> {
    try {
      const response = await axios.post(`${this.baseUrl}/kyc/refresh-token`, applicantId);

      if (response.data.success) {
        return {
          success: true,
          data: {
            applicantId,
            accessToken: response.data.data.accessToken
          }
        };
      } else {
        return {
          success: false,
          error: response.data.message || 'Failed to refresh access token'
        };
      }
    } catch (error: any) {
      console.error('KYC refresh token error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to refresh access token'
      };
    }
  }
}

export const kycService = new KYCService();
export type { KYCApplicantData, KYCResponse }; 